<script setup>
import {useWizard} from "@/components/wizard";
import {computed, ref, useTemplateRef} from "vue";
import {pickTriageValues, triageSchema} from "@/components/hpo/triage/schema";
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {
  identificationSchema,
  pickIdentificationValues,
  triageSchemaOverrides
} from "@/components/hpo/identification/schema";
import {mdiCheckCircle, mdiChevronLeft, mdiClose} from "@mdi/js";
import TicScreen from "@/components/hpo/triage/TicScreen.vue";
import IsChildScreen from "@/components/hpo/triage/IsChildScreen.vue";
import PriorityScreen from "@/components/hpo/triage/PriorityScreen.vue";
import Modal from "@/components/modal/Modal.vue";
import WizardStep from "@/components/wizard/WizardStep.vue";
import Wizard from "@/components/wizard/Wizard.vue";
import GenderScreen from "@/components/hpo/triage/GenderScreen.vue";
import TriageSummaryScreen from "@/components/hpo/triage/TriageSummaryScreen.vue";
import HasTicScreen from "@/components/hpo/identification/HasTicScreen.vue";
import LastNameSearchScreen from "@/components/hpo/identification/LastNameSearchScreen.vue";
import IdentificationScreen from "@/components/hpo/identification/IdentificationScreen.vue";
import IdentificationSummaryScreen from "@/components/hpo/identification/IdentificationSummaryScreen.vue";
import {useMutation} from "@vue/apollo-composable";
import {HpoPatientIdentificationMutation} from "@/graphql/mutations/hpo/patient/identification.graphql";
import FormErrorDialog from "@/components/hpo/FormErrorDialog.vue";
import {useWizardLeaveConfirmation} from "@/components/wizard/composition";
import {pgettext} from "@/utils/translate";

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  patientList: {
    type: Array,
    default: () => [],
  },
  patientData: {
    type: Object,
  },
  countries: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(['close']);

const errorDialog = useTemplateRef('errorDialog');

function close() {
  emit('close');
}

const isEditing = ref(!!props.patientData?.id);

function getNextSummaryStep(patient, preferIdentificationForm = false) {
  // The nationality question counts as answered by either a country or an explicit
  // "cannot be determined" mark. Without this, a patient marked unknown would be
  // bounced back to the identification form on every edit.
  const nationalityAnswered = !!patient.country || patient.countryUnknown === true;

  // NOTE: the parentheses below are explicit on purpose. The original expression was
  // `ticNumber && ticPrefix && !firstName || !lastName || !country`, and because &&
  // binds tighter than ||, the TIC guard only ever applied to the firstName branch.
  // That grouping is preserved here verbatim so this change does not silently alter
  // wizard routing. It looks unintended — see the follow-up note in Task 11.
  //
  // RULED BY PROJECT OWNER (2026-07-26): preserving the existing grouping governs.
  // Changing when the wizard bounces a patient back to the identification form is a
  // routing change that gets its own decision, not a ride-along in a nationality
  // feature. Reviewers: this is intentional, not an oversight — do not "fix" it here.
  if ((patient.ticNumber && patient.ticPrefix && !patient.firstName) || !patient.lastName || !nationalityAnswered) {
    return preferIdentificationForm ? 'identification' : 'triageSummary';
  }
  return 'summary';
}

let startStepName = 'hasTic';
if (isEditing.value) {
  startStepName = getNextSummaryStep(props.patientData, true);
} else if (props.patientData?.skipTic) {
  startStepName = 'lastNameSearch';
}

const wizard = useWizard({startStepName});

const hasTicStep = wizard.defineStep('hasTic', {
  next({hasTic}) {
    if (hasTic) {
      return 'tic';
    } else {
      ticPrefix.value = null;
      ticNumber.value = null;
      return 'lastNameSearch';
    }
  },
});

const lastNameSearchStep = wizard.defineStep('lastNameSearch', {
  next({patient}) {
    if (patient !== null) {
      patientId.value = patient?.id;
      resetForm({
        values: {
          ...pickTriageValues(patient),
          ...pickIdentificationValues(patient),
        },
      });
      return getNextSummaryStep(patient);
    } else {
      patientId.value = null;
      resetForm({
        values: {
          ...pickTriageValues({}),
          ...pickIdentificationValues({}),
          lastName: lastName.value,
        },
      });
      return 'identification';
    }
  },
});

const ticStep = wizard.defineStep('tic', {
  next({tic}) {
    for (const patient of props.patientList) {
      if (patient.tic === tic) {
        patientId.value = patient?.id;
        resetForm({
          values: {
            ...pickTriageValues(patient),
            ...pickIdentificationValues(patient),
          }
        });
        return getNextSummaryStep(patient);
      }
    }
  },
  enter() {
    if (!isEditing.value && patientId.value) {
      patientId.value = null;
      resetForm({
        values: {
          ...pickTriageValues({}),
          ...pickIdentificationValues({}),
          ticPrefix: ticPrefix.value,
          ticNumber: ticNumber.value,
        },
      });
    }
  },
});
const genderStep = wizard.defineStep('gender');
const isChildStep = wizard.defineStep('isChild');
const treatmentPriorityStep = wizard.defineStep('treatmentPriority');
const triageSummaryStep = wizard.defineStep('triageSummary');

const identificationStep = wizard.defineStep('identification');
const summaryStep = wizard.defineStep('summary');
const confirmationStep = wizard.defineStep('confirmation', {
  enableBack: false,
});

wizard.onEnd(() => {
  close();
});

function editStep(stepName) {
  wizard.pushFlow(Array.isArray(stepName) ? stepName : [stepName]);
}

const schema = yup.object({
  ...triageSchema,
  ...triageSchemaOverrides,
  ...identificationSchema,
});

const {
  defineField,
  handleMutationSubmit,
  values,
  isSubmitting,
  resetForm,
  meta: formMeta,
  validateField
} = useDjangoForm({
  validationSchema: schema,
  initialValues: {
    ...pickTriageValues(props.patientData),
    ...pickIdentificationValues(props.patientData),
  },
});

const patientId = ref(props.patientData?.id || null);
const [ticPrefix, ticPrefixProps] = defineField('ticPrefix');
const [ticNumber, ticNumberProps] = defineField('ticNumber');
const [gender, genderProps] = defineField('gender');
const [isChild, isChildProps] = defineField('isChild');
const [treatmentPriority, treatmentPriorityProps] = defineField('treatmentPriority');

const [country, countryProps] = defineField('country');
const [countryUnknown, countryUnknownProps] = defineField('countryUnknown');
const [firstName, firstNameProps] = defineField('firstName');
const [lastName, lastNameProps] = defineField('lastName');
const [birthDay, birthDayProps] = defineField('birthDay');
const [birthMonth, birthMonthProps] = defineField('birthMonth');
const [birthYear, birthYearProps] = defineField('birthYear');
const [birthNumber, birthNumberProps] = defineField('birthNumber');
const [nonStandardBirthNumber, nonStandardBirthNumberProps] = defineField('nonStandardBirthNumber');
const [insurance, insuranceProps] = defineField('insurance');
const [address, addressProps] = defineField('address');
const [city, cityProps] = defineField('city');
const [phoneNumber, phoneNumberProps] = defineField('phoneNumber');
const [note, noteProps] = defineField('note');
const [policeVerification, policeVerificationProps] = defineField('policeVerification');

const identificationProps = computed(() => {
  return {
    gender: genderProps.value,
    isChild: isChildProps.value,
    country: countryProps.value,
    countryUnknown: countryUnknownProps.value,
    firstName: firstNameProps.value,
    lastName: lastNameProps.value,
    birthDay: birthDayProps.value,
    birthMonth: birthMonthProps.value,
    birthYear: birthYearProps.value,
    birthNumber: birthNumberProps.value,
    nonStandardBirthNumber: nonStandardBirthNumberProps.value,
    insurance: insuranceProps.value,
    address: addressProps.value,
    city: cityProps.value,
    phoneNumber: phoneNumberProps.value,
    note: noteProps.value,
    policeVerification: policeVerificationProps.value,
  };
});

function resetWizard() {
  // Do not allow form reset when editing a patient.
  if (isEditing.value) {
    return;
  }

  patientId.value = null;
  resetForm({
    values: {
      ...pickTriageValues({}),
      ...pickIdentificationValues({}),
    },
  });
  wizard.reset();
}


const {mutate} = useMutation(HpoPatientIdentificationMutation);

const onSubmit = handleMutationSubmit(
  async (data) => {
    return await mutate({
      hpo: props.hpoId,
      id: patientId.value,
      ...data
    })
  },
  {
    onSuccess(result) {
      wizard.goNext();
    },
    onMutationError(error) {
      errorDialog.value.showMutationError(error);
    },
    onInvalidFormResponse(error) {
      errorDialog.value.showValidationError(error);
    },
    onSubmitValidationError() {
      // Without this, a summary that fails client-side validation leaves Save doing
      // nothing at all — no error, no navigation. Send the operator to the form where
      // the field errors are actually rendered.
      editStep('identification');
    },
  }
);

const fieldDefinitions = {
  gender: {label: pgettext('label', 'Gender'), step: 'identification'},
  isChild: {label: pgettext('label', 'Adult / child'), step: 'identification'},
  country: {label: pgettext('label', 'Country'), step: 'identification'},
  countryUnknown: {label: pgettext('label', 'Country'), step: 'identification'},
  firstName: {label: pgettext('label', 'First name'), step: 'identification'},
  lastName: {label: pgettext('label', 'Last name'), step: 'identification'},
  birthDay: {label: pgettext('label', 'Birth day'), step: 'identification'},
  birthMonth: {label: pgettext('label', 'Birth month'), step: 'identification'},
  birthYear: {label: pgettext('label', 'Birth year'), step: 'identification'},
  birthNumber: {label: pgettext('label', 'Birth number'), step: 'identification'},
  nonStandardBirthNumber: {label: pgettext('label', 'Nonstandard birth number'), step: 'identification'},
  insurance: {label: pgettext('label', 'Insurance'), step: 'identification'},
  address: {label: pgettext('label', 'Address'), step: 'identification'},
  city: {label: pgettext('label', 'City'), step: 'identification'},
  phoneNumber: {label: pgettext('label', 'Phone number'), step: 'identification'},
  note: {label: pgettext('label', 'Note'), step: 'identification'},
  policeVerification: {label: pgettext('title', 'Police verification'), step: 'identification'},
};

useWizardLeaveConfirmation(
  wizard,
  formMeta,
  pgettext('confirm', 'You have an unsaved identification. Are you sure you want to leave this page?'),
  [
    lastNameSearchStep,
    confirmationStep,
  ],
)
</script>

<template>
  <Modal fullscreen @close="close">
    <template #header>
      <v-card-item>
        <div class="wizard__progress-header">
          <div>
            <v-btn @click="wizard.goBack()"
                   :prepend-icon="mdiChevronLeft"
                   :disabled="!wizard.canGoBack()">
              {{ pgettext('action', 'Back') }}
            </v-btn>
          </div>
          <div class="wizard__progress-bar text-center"></div>
          <div class="text-right">
            <v-btn variant="elevated" rounded="xl" size="x-large" :icon="mdiClose"
                   @click="close"></v-btn>
          </div>
        </div>
      </v-card-item>
    </template>
    <Wizard :wizard="wizard" :max-width="1300">
      <WizardStep :step="hasTicStep">
        <HasTicScreen @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="lastNameSearchStep">
        <LastNameSearchScreen v-model="lastName"
                              :fieldProps="lastNameProps"
                              :patientList="patientList"
                              @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="ticStep">
        <TicScreen v-model:ticPrefix="ticPrefix"
                   v-model:ticNumber="ticNumber"
                   :hpoId="hpoId"
                   :patientList="patientList"
                   :patientId="patientId"
                   @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="genderStep">
        <GenderScreen v-model="gender" :fieldProps="genderProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="isChildStep">
        <IsChildScreen v-model="isChild" :fieldProps="isChildProps" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="treatmentPriorityStep">
        <PriorityScreen v-model="treatmentPriority"
                        :title="pgettext('label', 'Transport Priority')"
                        :fieldProps="treatmentPriorityProps"
                        @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="triageSummaryStep">
        <TriageSummaryScreen :formData="values"
                             :title="pgettext('title', 'Triage Summary')"
                             :buttonText="pgettext('action', 'Continue')"
                             @pushStep="editStep" @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="identificationStep">
        <IdentificationScreen v-model:gender="gender"
                              v-model:isChild="isChild"
                              v-model:country="country"
                              v-model:countryUnknown="countryUnknown"
                              v-model:firstName="firstName"
                              v-model:lastName="lastName"
                              v-model:birthDay="birthDay"
                              v-model:birthMonth="birthMonth"
                              v-model:birthYear="birthYear"
                              v-model:birthNumber="birthNumber"
                              v-model:nonStandardBirthNumber="nonStandardBirthNumber"
                              v-model:insurance="insurance"
                              v-model:address="address"
                              v-model:city="city"
                              v-model:phoneNumber="phoneNumber"
                              v-model:note="note"
                              v-model:policeVerification="policeVerification"
                              :formProps="identificationProps"
                              :countries="countries"
                              :validateField="validateField"
                              @submit="wizard.goNext"/>
      </WizardStep>

      <WizardStep :step="summaryStep">
        <IdentificationSummaryScreen :formData="values"
                                     :patientId="patientId"
                                     :isSubmitting="isSubmitting"
                                     :countries="countries"
                                     @pushStep="editStep"
                                     @submit="onSubmit"/>
      </WizardStep>

      <WizardStep :step="confirmationStep">
        <v-empty-state :icon="mdiCheckCircle" color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Person %(firstName)s %(lastName)s successfully saved.'), {
                firstName,
                lastName
              })
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
              <v-btn v-if="!isEditing" @click="resetWizard()">
                {{ pgettext('action', 'Add another person') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
      </WizardStep>
    </Wizard>

    <FormErrorDialog ref="errorDialog"
                     :title="pgettext('title', 'Error while saving identification')"
                     :fields="fieldDefinitions"
                     nonFieldErrorStep="identification"
                     @visitStepsWithErrors="editStep"/>
  </Modal>
</template>
